// client/src/utils/colorExtractor.js
// Utility to extract dominant color from an image

/**
 * Extract the dominant color from an image file
 * @param {File} imageFile - The image file to extract color from
 * @returns {Promise<string>} Hex color code (e.g., "#FF5733")
 */
export const extractDominantColor = (imageFile) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      try {
        // Set canvas size (smaller for faster processing)
        const size = 150; // Process at 150x150 for speed
        canvas.width = size;
        canvas.height = size;
        
        // Draw image to canvas
        ctx.drawImage(img, 0, 0, size, size);
        
        // Get image data
        const imageData = ctx.getImageData(0, 0, size, size);
        const data = imageData.data;
        
        // Calculate color frequencies
        const colorMap = {};
        const step = 4; // Process every 4th pixel for speed
        
        for (let i = 0; i < data.length; i += step * 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          
          // Skip transparent or very dark pixels
          if (a < 128 || (r < 30 && g < 30 && b < 30)) continue;
          
          // Quantize colors to reduce noise (group similar colors)
          const quantizedR = Math.floor(r / 10) * 10;
          const quantizedG = Math.floor(g / 10) * 10;
          const quantizedB = Math.floor(b / 10) * 10;
          
          const colorKey = `${quantizedR},${quantizedG},${quantizedB}`;
          colorMap[colorKey] = (colorMap[colorKey] || 0) + 1;
        }
        
        // Find the most frequent color
        let maxCount = 0;
        let dominantColor = null;
        
        for (const [colorKey, count] of Object.entries(colorMap)) {
          if (count > maxCount) {
            maxCount = count;
            dominantColor = colorKey;
          }
        }
        
        if (dominantColor) {
          const [r, g, b] = dominantColor.split(',').map(Number);
          // Convert to hex
          const hex = '#' + [r, g, b].map(x => {
            const hex = x.toString(16);
            return hex.length === 1 ? '0' + hex : hex;
          }).join('');
          
          // Lighten the color slightly for better readability as background
          const lightened = lightenColor(hex, 0.3);
          resolve(lightened);
        } else {
          // Fallback to a neutral color
          resolve('#F5F5F5');
        }
      } catch (error) {
        console.error('Error extracting color:', error);
        resolve('#F5F5F5'); // Fallback to neutral gray
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Create object URL from file
    img.src = URL.createObjectURL(imageFile);
  });
};

/**
 * Lighten a hex color by a percentage
 * @param {string} hex - Hex color code
 * @param {number} percent - Percentage to lighten (0-1)
 * @returns {string} Lightened hex color
 */
const lightenColor = (hex, percent) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, ((num >> 16) & 0xff) + Math.floor(255 * percent));
  const g = Math.min(255, ((num >> 8) & 0xff) + Math.floor(255 * percent));
  const b = Math.min(255, (num & 0xff) + Math.floor(255 * percent));
  
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

